// One running Day Care session: the course's instructions, the dialysis chart,
// the kit used, the discharge steps and — if the stay runs long — conversion to
// an inpatient stay. Opened from the board as /daycare/session/:id?unit=&date=,
// so a reload re-reads the same day's board and finds the session in it.

import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import DaycareScreen from "../components/DaycareScreen";
import DialysisChart from "../components/DialysisChart";
import ConsumptionPanel from "../components/ConsumptionPanel";
import DischargePanel from "../components/DischargePanel";
import ConvertPanel from "../components/ConvertPanel";
import ToneChip from "../components/ToneChip";
import { fetchDay } from "../api/daycareApi";
import { groupLabel, statusChip } from "../utils/sessionView";
import { formatIstDate } from "../../../utils/istDateTime";
import { IPD_COLORS, IpdButton, IpdDetailRow, IpdSectionCard, ipdCardSx } from "../../ipd/utils";
import { ADMISSION_STATUS, CHART_TYPE, DAYCARE_BASE, SESSION_STATUS } from "../constants";

/** The patient strip, laid out like IPD Billing's "Selected patient" card. */
function PatientBanner({ session, shift, onBack }) {
  const chip = statusChip(session);
  const facts = [
    `ID: ${session.patient_id}`,
    session.admission_id ? `Admission #${session.admission_id}` : "Not admitted",
    `Machine ${session.machine_label}`,
    shift ? `${shift.label} ${shift.start}–${shift.end}` : session.shift_code,
    formatIstDate(session.session_date),
  ];
  return (
    <Box sx={{ ...ipdCardSx, px: 2.5, py: 2, mb: 2.5, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
      <Box sx={{ flex: 1, minWidth: 240 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, color: IPD_COLORS.textSecondary, textTransform: "uppercase" }}>
          Selected patient
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: IPD_COLORS.textPrimary }}>
            {session.patient_name || session.patient_id}
          </Typography>
          <ToneChip tone={chip.tone} label={chip.label} />
        </Box>
        <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary, mt: 0.25 }}>{facts.join(" · ")}</Typography>
        <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary }}>
          {`Machines: ${groupLabel(session.patient_group)}${session.retest_due ? " · markers retest due" : ""}`}
        </Typography>
      </Box>
      <IpdButton variant="outlined" onClick={onBack}>Back to board</IpdButton>
    </Box>
  );
}

export default function DaycareSession() {
  const history = useHistory();
  const { id } = useParams();
  const query = new URLSearchParams(useLocation().search);
  const unitId = query.get("unit");
  const date = query.get("date");
  const [day, setDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      setDay(await fetchDay(unitId, date));
    } catch (err) {
      setError(err?.body?.message || "Could not load the session");
    } finally {
      setLoading(false);
    }
  }, [unitId, date]);

  useEffect(() => { load(); }, [load]);

  const session = (day?.sessions || []).find((s) => String(s.id) === String(id));
  const unit = day?.unit;
  const shift = unit?.shifts?.find((s) => s.code === session?.shift_code);
  const discharged = session?.admission_status === ADMISSION_STATUS.DISCHARGED;
  const running = session?.status === SESSION_STATUS.ADMITTED && !discharged;
  const charted = session && [SESSION_STATUS.ADMITTED, SESSION_STATUS.CONVERTED].includes(session.status);

  return (
    <DaycareScreen
      title="Session"
      description={session ? `${session.service_name} on ${unit?.name || "the unit"}` : ""}
      loading={loading}
      error={error || (!loading && day && !session ? "This session is not on that day's board" : "")}
      onRetry={load}
    >
      {session && (
        <>
          <PatientBanner session={session} shift={shift} onBack={() => history.push(DAYCARE_BASE)} />

          <Box sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 2fr) minmax(320px, 1fr)" },
            gap: 2.5,
            alignItems: "start",
          }}
          >
            <Box sx={{ minWidth: 0 }}>
              {charted && unit?.chart_type === CHART_TYPE.DIALYSIS && (
                <DialysisChart session={session} readOnly={discharged} />
              )}
              {running && <ConsumptionPanel session={session} />}
              {!session.admission_id && (
                <IpdSectionCard title="Not admitted yet">
                  <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
                    Check this session in from the board; the chart opens once the patient is on the machine.
                  </Typography>
                </IpdSectionCard>
              )}
            </Box>

            <Box sx={{ minWidth: 0, display: "grid", gap: 2 }}>
              <IpdSectionCard title="Course order" subtitle={`Ordered by ${session.ordered_by_name || "—"}`}>
                <IpdDetailRow label="Service" value={session.service_name} />
                <IpdDetailRow label="Machine group" value={groupLabel(session.patient_group)} />
                <IpdDetailRow label="Admission" value={session.admission_id
                  ? `#${session.admission_id} · ${session.admission_status}` : "Not admitted"} />
                <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: IPD_COLORS.panelHeaderBg, border: `1px solid ${IPD_COLORS.border}` }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: IPD_COLORS.textSecondary, textTransform: "uppercase", letterSpacing: 0.6, mb: 0.5 }}>
                    Instructions
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: IPD_COLORS.textPrimary }}>
                    {session.instructions || "No instructions on the order."}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textSecondary, mt: 1 }}>
                  EPO, iron and other patient-specific drugs are ordered in the IPD doctor console.
                </Typography>
              </IpdSectionCard>

              {running && <DischargePanel session={session} shiftEnd={shift?.end} onChanged={load} />}
              {discharged && (
                <IpdSectionCard title="Discharge">
                  <Box sx={{ display: "flex", gap: 1.25, alignItems: "center" }}>
                    <CheckCircleIcon sx={{ color: IPD_COLORS.success.text }} />
                    <Typography sx={{ fontSize: 13, color: IPD_COLORS.textPrimary }}>
                      Physically discharged. The machine has gone to cleaning.
                    </Typography>
                  </Box>
                </IpdSectionCard>
              )}
              {running && <ConvertPanel session={session} wardBedType={unit?.bed_type_code} onConverted={load} />}
            </Box>
          </Box>
        </>
      )}
    </DaycareScreen>
  );
}
